import { useCallback, useEffect, useRef, useState } from "react";
import { IonModal, IonButton } from "@ionic/react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { getUpdateStatus, performUpdate } from "../../services/appUpdate";
import { diagEvent } from "../../services/diag";
import dsPictogram from "../../assets/images/ds-pictogram.svg";
import "./ForceUpdateGate.css";

// Don't hammer the store lookup: ignore checks fired within this window.
const CHECK_THROTTLE_MS = 30_000;

/**
 * App-wide blocking gate. On launch and on every resume from background it
 * checks the store for a newer version; if one exists it shows a
 * non-dismissable modal that blocks all access until the user updates.
 * Native-only — a no-op on web/PWA. Fail-open: never blocks on errors.
 */
const ForceUpdateGate = () => {
  const [status, setStatus] = useState(null);
  const lastCheck = useRef(0);
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (Capacitor.getPlatform() === "web") return;
    if (checking.current) return;
    const now = Date.now();
    if (now - lastCheck.current < CHECK_THROTTLE_MS) return;

    checking.current = true;
    lastCheck.current = now;
    try {
      const s = await getUpdateStatus();
      // DIAG: report EXACTLY what the iOS iTunes lookup returned on this check —
      // whether the gate opens (`avail`), the installed version (`cur`) and the
      // store version (`store`). `cur` absent + avail=0 => the lookup threw and
      // fail-opened; cur===store + avail=0 => genuinely up to date. This tells us
      // from real devices whether ForceUpdateGate is even involved in the freeze.
      diagEvent("diag_update_check", {
        avail: s.updateAvailable ? 1 : 0,
        cur: String(s.currentVersion || "").slice(0, 40),
        store: String(s.availableVersion || "").slice(0, 40),
        plat: String(s.platform || "").slice(0, 16),
      });
      // Block when an update is available; clear once we're up to date again
      // (or on a fail-open "no update" result).
      setStatus(s.updateAvailable ? s : null);
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    check(); // on launch

    let listener;
    App.addListener("appStateChange", (state) => {
      if (state.isActive) check();
    }).then((l) => {
      listener = l;
    });

    return () => {
      if (listener) listener.remove();
    };
  }, [check]);

  // DIAG: record open/close of the force-update gate + the app's visibility
  // state, to catch it presenting/dismissing while backgrounded (paused rAF ->
  // stuck animation -> orphaned backdrop).
  useEffect(() => {
    diagEvent("diag_gate", {
      gate: "forceupdate",
      open: status?.updateAvailable ? 1 : 0,
      hidden: typeof document !== "undefined" && document.hidden ? 1 : 0,
    });
  }, [status]);

  const open = Boolean(status?.updateAvailable);

  return (
    <IonModal
      isOpen={open}
      backdropDismiss={false}
      // Force-update block, WITHOUT the stuck-backdrop bug. A *static*
      // canDismiss={false} blocks the framework's own programmatic close too:
      // when the store check flaps false after a resume, `open` goes true→false,
      // Ionic tries to dismiss, canDismiss says "no", and it bails before teardown
      // — STRANDING the ion-backdrop (dimmed Home, every tap swallowed = the
      // "frozen after update" bug). The FUNCTION form fixes this while keeping the
      // block: while an update is available it returns false (blocks backdrop tap,
      // swipe, AND Android hardware-back — users on an old build stay hard-locked);
      // once the check clears, `updateAvailable` is falsy so it returns true and
      // the modal tears down cleanly. `open` and this both read the same `status`,
      // so they never disagree.
      canDismiss={() => !status?.updateAvailable}
      className="force-update-modal"
    >
      <div className="force-update-content">
        <img
          src={dsPictogram}
          alt="Dieta Social"
          className="force-update-logo"
        />
        <h2 className="force-update-title">È disponibile un aggiornamento</h2>
        <p className="force-update-body">
          Per continuare a usare Dieta Social, aggiorna l&apos;app
          all&apos;ultima versione. Bastano pochi istanti.
        </p>
        <IonButton
          expand="block"
          className="force-update-btn"
          onClick={() => performUpdate(status)}
        >
          Aggiorna ora
        </IonButton>
      </div>
    </IonModal>
  );
};

export default ForceUpdateGate;
